import { useCallback, useRef } from 'react';

/**
 * Swipe horizontal para cambiar de tab.
 * Solo cuenta un gesto claramente horizontal, corto y con recorrido,
 * para no pisar el scroll vertical, sliders, switches ni un drag de reordenado.
 */
const CONTROL_SEL = [
  'input',
  'textarea',
  'select',
  'option',
  '[contenteditable="true"]',
  '.MuiSwitch-root',
  '.MuiSlider-root',
  '[role="switch"]',
  '[role="slider"]',
].join(', ');

type SwipeStart = { x: number; y: number; time: number };

export function useHorizontalTabSwipe(options: {
  tabCount: number;
  activeIndex: number;
  onIndexChange: (next: number) => void;
  /** Si devuelve true, este toque no cambia de tab (editor anidado, modo editar, etc.). */
  isBlocked?: () => boolean;
  /** Selectores extra (además de inputs, switches y sliders) que anulan el gesto. */
  extraIgnoreSelector?: string;
}) {
  const swipeStartRef = useRef<SwipeStart | null>(null);
  const tabCountRef = useRef(options.tabCount);
  const activeIndexRef = useRef(options.activeIndex);
  const onIndexChangeRef = useRef(options.onIndexChange);
  const isBlockedRef = useRef(options.isBlocked);
  const extraIgnoreRef = useRef(options.extraIgnoreSelector);

  tabCountRef.current = options.tabCount;
  activeIndexRef.current = options.activeIndex;
  onIndexChangeRef.current = options.onIndexChange;
  isBlockedRef.current = options.isBlocked;
  extraIgnoreRef.current = options.extraIgnoreSelector;

  const gestureBlocked = useCallback((target: EventTarget | null) => {
    if (document.documentElement.classList.contains('shc-dnd-lock')) return true;
    if (isBlockedRef.current?.()) return true;
    if (!(target instanceof Element)) return false;
    if (target.closest(CONTROL_SEL)) return true;
    const extra = extraIgnoreRef.current;
    if (extra && target.closest(extra)) return true;
    return false;
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1 || gestureBlocked(e.target)) {
      swipeStartRef.current = null;
      return;
    }
    const touch = e.touches[0];
    swipeStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  }, [gestureBlocked]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const start = swipeStartRef.current;
    if (!start || !e.touches[0]) return;
    const deltaX = Math.abs(e.touches[0].clientX - start.x);
    const deltaY = Math.abs(e.touches[0].clientY - start.y);
    const clearlyHorizontal = deltaX >= 52 && deltaX > deltaY * 2;
    if (clearlyHorizontal) {
      e.preventDefault();
    }
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start || !e.changedTouches[0]) return;
    if (gestureBlocked(e.target)) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = Math.abs(touch.clientY - start.y);
    const deltaTime = Date.now() - start.time;
    const absDeltaX = Math.abs(deltaX);

    if (!(absDeltaX > deltaY * 2 && absDeltaX > 90 && deltaTime < 500 && deltaTime > 50)) {
      return;
    }

    const current = activeIndexRef.current;
    const last = tabCountRef.current - 1;
    if (deltaX > 0 && current > 0) {
      onIndexChangeRef.current(current - 1);
    } else if (deltaX < 0 && current < last) {
      onIndexChangeRef.current(current + 1);
    }
  }, [gestureBlocked]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
